import React, { memo } from 'react';
import { BalloonEventModel } from 'ts-core';
import { EventTile } from './EventTile';
import { LazyVisible } from './LazyVisible';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useBalloonEventsData } from '../contexts/EventData';
import { useBottomSheetContext } from "../hooks/useBottomSheet.ts";
import { useIsMobile } from "react-ui";
import { useTileSize } from "../hooks/use-tile-size";

export interface EventTilesListProps {
    events: BalloonEventModel[];
    onLoadMore: () => void;
    hasMore: boolean;
    isLoading?: boolean;
}

export const EventTilesList: React.FC<EventTilesListProps> = memo(function EventTilesList({
    events,
    onLoadMore,
    hasMore,
    isLoading = false,
}) {
    const { selectedEvent } = useBalloonEventsData();
    const bottomSheetContext = useBottomSheetContext();
    const isMobile = useIsMobile()
    const tileSize = useTileSize();
    const sentinelRef = useInfiniteScroll({
        onLoadMore,
        hasMore,
        isLoading,
        threshold: 200,
    });

    if (events.length === 0 && !isLoading) {
        return (
            <div className="events-tiles-list w-full h-full flex items-center justify-center">
                <p className="text-Text text-center">No events found</p>
            </div>
        );
    }
    const bottomSheetState = isMobile ? bottomSheetContext?.state : undefined;
    return (
        <div className="events-tiles-list">
            <div
                className={`px-[18px] gap-4 ${bottomSheetState === "half"
                        ? "flex flex-nowrap pt-1 pb-3"
                        : "grid grid-cols-2 py-4"
                    }`}
                style={{
                    width: bottomSheetState === "half" ? `${events.length * 180}px` : "100%",
                }}
            >
                {events.map((event) => (
                    <LazyVisible
                        key={event.id}
                        // EventTile renders at TILE_SIZE_PX square, so reserve
                        // that exact footprint while the tile is off-screen —
                        // keeps the grid layout and scroll positions stable.
                        placeholderHeight={tileSize}
                        placeholderWidth={tileSize}
                    >
                        <EventTile
                            event={event}
                            selected={selectedEvent?.id === event.id}
                            hasSelectedEvent={!!selectedEvent}
                        />
                    </LazyVisible>
                ))}

                {/* Infinite Scroll Sentinel */}
                {hasMore && (
                    <div ref={sentinelRef} className="w-full h-1" />
                )}

                {/* Loading Indicator */}
                {isLoading && (
                    <div className="w-full flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-Balloon"></div>
                    </div>
                )}
            </div>
        </div>
    );
});
